import { useCallback, useId, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { Button, type ButtonSize, type ButtonVariant } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { ActionMenu, type ActionMenuItem, type MenuPlacement } from "../Menu/ActionMenu.js";
import { useMenuDismiss } from "../Menu/useMenuDismiss.js";
import styles from "./SplitButton.module.scss";

/** One entry in the caret menu — the shared menu item, named for this component's callers. */
export type SplitButtonAction = ActionMenuItem;

export interface SplitButtonProps {
  /** The primary action's label. */
  children: ReactNode;
  onClick: () => void;
  /**
   * The secondary actions. **Empty renders a plain `Button`** — a caret opening an empty menu is worse
   * than no caret, and every caller so far has a state where the extra actions do not apply (a confirmed
   * show has no hold to promote).
   */
  actions: SplitButtonAction[];
  /** Accessible name for the caret half — say what the menu holds, not "more". */
  menuLabel: string;
  /**
   * Which way the menu opens. `below` (default) suits a header; `above` is for a control pinned to the
   * bottom of its container — a dialog's footer ribbon, where a downward menu falls off the card.
   */
  placement?: MenuPlacement;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
}

/**
 * `SplitButton` — one pill split in two: a primary action, a hairline divider, and a caret opening a menu of
 * related secondary actions.
 *
 * Extracted 2026-08-23 from Booking's "+ Add show" header control and the show editor's footer Save, which
 * had each hand-rolled the same ~20 lines of dismiss behaviour: outside-click close, Escape close with focus
 * returned to the caret, focus moved to the first item on open. That effect is the part worth owning
 * centrally — it is invisible when right and a keyboard trap when wrong, and a third copy was the likely
 * next step.
 *
 * **Escape closes the MENU only.** The listener runs in the capture phase and stops propagation, so a
 * SplitButton inside a dialog does not close the dialog on the first Escape — the overlay stack unwinds one
 * layer at a time, which is the rule `Modal`/`AcDialog` already own.
 *
 * The geometry is the shipped one: inner corners flattened, a 1px gap between the halves, so it reads as one
 * control with a secondary action rather than two buttons that happen to touch.
 */
export function SplitButton({
  children,
  onClick,
  actions,
  menuLabel,
  placement = "below",
  variant,
  size,
  loading,
  disabled,
  className,
}: SplitButtonProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const caretRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const hasMenu = actions.length > 0;
  const close = useCallback(() => setOpen(false), []);
  useMenuDismiss(open, close, rootRef, caretRef);

  // No secondary actions ⇒ no split. Degrading to a plain Button keeps the caller from having to branch.
  if (!hasMenu) {
    return (
      <Button variant={variant} size={size} loading={loading} disabled={disabled} className={className} onClick={onClick}>
        {children}
      </Button>
    );
  }

  return (
    <div className={clsx(styles.split, className)} ref={rootRef}>
      <Button
        variant={variant}
        size={size}
        loading={loading}
        disabled={disabled}
        className={styles.main}
        onClick={onClick}
      >
        {children}
      </Button>
      <Button
        ref={caretRef}
        variant={variant}
        size={size}
        disabled={disabled || loading}
        className={styles.caret}
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon icon={ChevronDown} size={16} />
      </Button>
      {open && <ActionMenu id={menuId} items={actions} placement={placement} onSelected={close} />}
    </div>
  );
}
